/* storybook-check-ignore */
import React, { useEffect, useState } from 'react';
import LogInConnector, { AccountProps } from '../../shared/AccountConnector';
import { COSMOS_URL } from '../../globals';
import { useObservability } from '../../../helpers/observability';
import { Box, Flex, Image, BoxProps, Icon, Button } from '@opendoor/bricks/core';
import { useRouter } from 'next/router';
import HeaderMenu from './HeaderMenu';
import dynamic from 'next/dynamic';
import SelectiveSpritesheet from '@opendoor/bricks/core/Icon/SelectiveSpritesheet';
import MenuIcon from '@opendoor/bricks/core/Icon/SpritesheetIcons/Menu';
import FavoriteIcon from '@opendoor/bricks/core/Icon/SpritesheetIcons/Favorite';
import { getNavLinkStyles, NavLink, NavLinkProps } from './NavLink';
import { useTheme } from '@opendoor/bricks/system/theme';
import { useExclusivesTypeContext } from '../../../helpers/exclusives/ExclusivesTypeContext';
import { useDevice } from '@opendoor/bricks/hooks/useMedia';

export interface Props {
  navMenuButtonId?: string;
  navMenuId?: string;
  extraNavLinks?: Array<NavLinkProps>;
  hideHeaderNavLinks?: boolean;
  logoToGallery?: boolean;
  maxWidth?: BoxProps['maxWidth'];
  openLinkOnNewTab?: boolean;
  shouldShowSearch?: boolean;
  variant?: 'default' | 'transparent';
  shouldHeaderOverlay?: boolean;
  onFAQsClick?: () => void;
}

const HeaderSearchbar = dynamic(() => import('./HeaderSearchbar'), {
  ssr: false,
});

const Container: React.FC<BoxProps & { variant: Props['variant']; isOverlay?: boolean }> = ({
  variant,
  isOverlay,
  ...props
}) => (
  <Box
    as="header"
    backgroundColor="neutrals0"
    position="sticky"
    top={0}
    left={0}
    zIndex={8}
    width="100%"
    transition="background-color 0.3s ease"
    borderBottom={variant === 'transparent' ? 'none' : '1px solid var(--colors-neutrals40)'}
    bgColor={isOverlay || variant === 'default' ? 'neutrals0' : 'transparent'}
    boxShadow={isOverlay && variant === 'transparent' ? 'z1' : 'none'}
    {...props}
  />
);

export const HEADER_HEIGHT = 72;

export const Header: React.FC<Props & AccountProps> = ({
  maybeFetchSessionInfo,
  maybeFetchSessionLeads,
  loggedIn,
  hideHeaderNavLinks,
  human,
  logoToGallery,
  leads,
  maybeLogOut,
  maxWidth = 'var(--header-max-width)',
  shouldShowSearch,
  openLinkOnNewTab,
  extraNavLinks = [],
  variant = 'default',
  shouldHeaderOverlay,
  onFAQsClick,
}) => {
  const { type } = useExclusivesTypeContext();
  const {
    colors: { brand40 },
  } = useTheme();
  const router = useRouter();
  const { trackEvent } = useObservability();
  const [isExpanded, setIsExpanded] = useState(false);
  const analyticsPrefix = `cosmos-${type}-header`;

  const fullName = human?.get('full_name');
  const firstName = fullName ? fullName.split(' ')[0] : '';
  const telephone = type === 'exclusives' ? '512-706-9938' : '972-362-9254';

  useEffect(() => {
    maybeFetchSessionInfo();
    maybeFetchSessionLeads();
  }, []);

  const handleAccountClick = () => {
    // TODO: remove categoryOverride
    // observability is initialized with 'cosmos' as the category for all events
    // NOTE: this is also tracked in Journey as `cosmos-{exclusives|exclusive-offers}-header-{loggedIn ? account : sign-in}`
    trackEvent('cta-click', 'account-icon', undefined, { categoryOverride: 'homepage' });
    if (!loggedIn) {
      document.location.href = `${COSMOS_URL}/exclusives/login?redirectTo=${encodeURIComponent(
        router.asPath,
      )}`;
    } else {
      setIsExpanded(!isExpanded);
    }
  };

  const handleExpandedState = (state: boolean) => () => {
    setIsExpanded(state);
  };

  return (
    <Container variant={variant} isOverlay={shouldHeaderOverlay}>
      <SelectiveSpritesheet icons={[FavoriteIcon, MenuIcon]} />
      <Flex as="header" maxWidth={maxWidth} mx="auto" height={HEADER_HEIGHT} px={[24, 40]}>
        <Flex flexGrow={1} justifyContent="space-between">
          <ExclusivesLogo logoToGallery={logoToGallery} />
          <Flex alignItems="center" justifyContent="center" gridGap="1rem">
            {shouldShowSearch && <HeaderSearchbar openLinkOnNewTab={openLinkOnNewTab} />}
            {type === 'exclusives' && (
              <>
                <NavLink
                  href="/exclusives/gallery"
                  aria-label="Go to Gallery"
                  analyticsName={`${analyticsPrefix}-gallery`}
                >
                  Gallery
                </NavLink>
                {loggedIn && (
                  <NavLink
                    borderRadius="roundedSquare"
                    display={['none', 'none', 'flex']}
                    alignItems="center"
                    href="/exclusives/saved-homes"
                    aria-label="Go to your homes"
                    gridGap="0.25rem"
                    analyticsName={`${analyticsPrefix}-saved-homes`}
                    _hover={{
                      backgroundColor: 'neutrals10',
                    }}
                  >
                    <Icon name="favorite" size={16} color="currentColor" /> Your homes
                  </NavLink>
                )}
                <NavLink
                  aria-label="FAQ"
                  href="/exclusives/faq"
                  display={['none', null, 'block']}
                  analyticsName={`${analyticsPrefix}-FAQs`}
                >
                  FAQ
                </NavLink>
              </>
            )}
            {extraNavLinks.map(NavLink)}
          </Flex>
          <Flex
            lineHeight="s3"
            gridGap={['1rem', '1.5rem']}
            minWidth={['28px', null, null, '203px']}
            alignItems="center"
            flexGrow={0}
            flexShrink={1}
          >
            {type === 'exclusive-offers' && (
              <Button
                display={['none', null, null, 'inherit']}
                variant="inline-secondary-link"
                sx={getNavLinkStyles()}
                analyticsName={`${analyticsPrefix}-faqs`}
                aria-label={null}
                onClick={() => onFAQsClick && onFAQsClick()}
              >
                FAQ
              </Button>
            )}

            <ExclusivesNavLink display={[loggedIn ? 'none' : 'block', null, null, 'block']} />
            {!hideHeaderNavLinks && (
              <>
                <NavLink
                  href={`tel:+1${telephone.replace(/-/g, '')}`}
                  display={['none', null, null, 'flex']}
                  aria-label={`Call us ${telephone}`}
                  analyticsName={`${analyticsPrefix}-contact-phone`}
                >
                  Call: {telephone}
                </NavLink>

                <Flex
                  alignItems="center"
                  position={[null, null, null, 'relative']}
                  flexGrow={0}
                  flexShrink={1}
                >
                  <Button
                    display={['none', null, 'initial']}
                    variant="inline-secondary-link"
                    sx={getNavLinkStyles()}
                    overflow="hidden"
                    textOverflow="ellipsis"
                    maxWidth={[110, 120, 'unset']}
                    aria-label={null}
                    analyticsName={`${analyticsPrefix}-${loggedIn ? 'account-menu' : 'sign-in'}`}
                    onClick={handleAccountClick}
                    px={3}
                  >
                    {loggedIn ? `Hi, ${firstName}` : 'Sign in'}
                  </Button>
                  <Button
                    aria-label="Open header menu"
                    analyticsName={`${analyticsPrefix}-menu-open`}
                    variant="icon"
                    display="inline-flex"
                    boxShadow="none"
                    border="none"
                    backgroundColor="transparent"
                    _hover={{ backgroundColor: `${brand40}40` }}
                    onClick={handleExpandedState(true)}
                  >
                    <Icon name="menu" size={16} />
                  </Button>
                  <HeaderMenu
                    human={human}
                    leads={leads}
                    loggedIn={loggedIn}
                    isExpanded={isExpanded}
                    maybeLogOut={maybeLogOut}
                    onClose={handleExpandedState(false)}
                    onFAQsClick={onFAQsClick}
                  />
                </Flex>
              </>
            )}
          </Flex>
        </Flex>
      </Flex>
    </Container>
  );
};

/**
 * Component should render the link for the opposite page
 * /exclusives -> Sell with Exclusives
 * /exclusives/offers -> Buy with Exclusives
 */
export const ExclusivesNavLink: React.FC<
  Omit<NavLinkProps, 'aria-label' | 'analyticsName' | 'href'>
> = (props) => {
  const { type } = useExclusivesTypeContext();
  const { isMobile } = useDevice();
  return (
    <NavLink
      {...props}
      aria-label={null}
      href={type === 'exclusives' ? '/exclusives/offers' : '/exclusives'}
      analyticsName={`cosmos-${type}-header-${type === 'exclusives' ? 'sell' : 'buy'}`}
    >
      {type === 'exclusives' ? 'Sell' : 'Buy'}
      {isMobile ? '' : ' with Exclusives'}
    </NavLink>
  );
};

const ExclusivesLogo: React.FC<Pick<Props, 'logoToGallery'>> = ({ logoToGallery }) => {
  const { type } = useExclusivesTypeContext();
  return (
    <NavLink
      analyticsName={`cosmos-${type}-header-logo`}
      trackImpression
      aria-label={null}
      href={logoToGallery ? '/exclusives/gallery' : '/exclusives'}
      rel="home"
      my="auto"
    >
      <Image
        display={['none', null, 'block']}
        maxWidth={['175px', null, '100%']}
        mt={[0, null, 2]}
        src="https://imgdrop.imgix.net/df3f4c2922ba4c4dbcc2f8142f9b3dbe.svg"
        alt="Opendoor Exclusives logo"
      />
      <Image
        maxWidth={['24px', null, '28px']}
        maxHeight={['24px', null, '28px']}
        display={['block', null, 'none']}
        src="https://imgdrop.imgix.net/0bee6386b0ca470b8a00382ed6adbfa3.svg"
        alt="Opendoor Exclusives logo"
      />
    </NavLink>
  );
};

export default LogInConnector(Header);
